import { api, thunkLoader, thunks } from "@app/api";
import { createSelector, delay, select } from "@app/fx";
import { defaultEntity, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployCustomerControl,
  DeployCustomerControlResource,
  DeployGlobalControl,
  DeployGlobalControlFramework,
  LinkResponse,
  SecurityControlStatus,
} from "@app/types";
import { DateTime } from "luxon";

import { selectRolesByOrgId } from "@app/roles";
import { selectEnvironmentsAsList } from "../environment";
import { selectPermissionsAsList } from "../permission";

const DEDICATED_STACK_ONLY_CONTROL_HANDLES = [
  "host-intrusion-detection",
  "network-intrusion-detection",
  "drift-detection-and-protection",
  "24-7-site-reliability-and-incident-response",
  "secure-isolated-network-connections-through-vpns-and-vpcs",
  "secure-network-architecture",
  // Requires dedicated stacks; N/A on shared (development) environments.
  "network-segregation",
];

const PREMIUM_CONTROL_HANDLES = ["host-intrusion-detection"];

const COMING_SOON_HANDLES = ["web-application-vulnerability-scanning"];

const ORGANIZATION_SCOPED_CONTROL_HANDLES = [
  "sso-integration",
  "multi-factor-authentication",
];

const CUSTOM_IMPLEMENTATION_COMPONENTS: Record<string, string> = {
  "multi-factor-authentication": "mfa",
  "sso-integration": "sso",
  "network-intrusion-detection": "networkIds",
  "host-intrusion-detection": "hids",
  "web-application-vulnerability-scanning": "webAppScanning",
};

const RESOURCE_TYPE_DISPLAY: Record<string, string> = {
  Account: "Environment",
  Vhost: "Endpoint",
  MfaControlProxyUser: "User",
};

const FRAMEWORK_DESCRIPTIONS: Record<
  string,
  { name: string; description: string; logo?: string }
> = {
  all: {
    name: "All Controls",
    description:
      "The full list of security and availability controls provided by Aptible",
  },
  "hipaa-required": {
    name: "HIPAA Required Controls",
    description:
      'The controls that satisfy the "Required" requirements of the HIPAA Security Rule',
    logo: "hipaa.png",
  },
  "hipaa-addressable": {
    name: "HIPAA Addressable Controls",
    description:
      'The controls that satisfy the "Addressable" requirements of the HIPAA Security Rule',
    logo: "hipaa.png",
  },
  "hitrust-inheritable": {
    name: "HITRUST Inheritable Controls",
    description:
      "The controls inheritable under Aptible's HITRUST CSF Validated Assessment",
    logo: "hitrust.png",
  },
};

export const MAX_SECURITY_CONTROL_SCOPE = 5;

interface DeployGlobalControlFrameworkResponse {
  id: string | number;
  name?: string;
  category: string;
  _embedded?: {
    framework?: {
      id: string | number;
      name: string;
    };
  };
  _type: "global_control_framework";
}

interface DeployGlobalControlResponse {
  id: string | number;
  name: string;
  handle: string;
  description: string;
  area: string;
  call_to_action?: string | null;
  callToAction?: string | null;
  detailed_description?: string | null;
  detailedDescription?: string | null;
  has_remediation?: boolean;
  hasRemediation?: boolean;
  custom_frontend?: boolean;
  customFrontend?: boolean;
  _embedded?: {
    global_control_frameworks?: DeployGlobalControlFrameworkResponse[];
  };
  _type: "global_control";
}

interface DeployCustomerControlResourceResponse {
  id: string | number;
  compliant: boolean;
  ignored: boolean;
  details_text?: string;
  detailsText?: string;
  resource_handle?: string;
  resourceHandle?: string;
  resource_id?: string | number;
  resourceId?: string | number;
  resource_type?: string;
  resourceType?: string;
  readable_resource_type?: string;
  readableResourceType?: string;
  _type: "customer_control_resource";
  _links?: {
    customer_control?: LinkResponse;
  };
}

interface DeployCustomerControlResponse {
  id: string | number;
  status: string;
  last_evaluation?: string | null;
  lastEvaluation?: string | null;
  _links: {
    self: LinkResponse;
    account: LinkResponse;
    global_control: LinkResponse;
  };
  _embedded?: {
    global_control?: DeployGlobalControlResponse;
    customer_control_resources?: DeployCustomerControlResourceResponse[];
  };
  _type: "customer_control";
}

const pluralize = (word: string, count: number) => {
  if (count === 1) return word;

  // Words ending in s, x, z, ch, sh → add "es"
  if (
    word.endsWith("s") ||
    word.endsWith("x") ||
    word.endsWith("z") ||
    word.endsWith("ch") ||
    word.endsWith("sh")
  ) {
    return `${word}es`;
  }

  // Words ending in "y" after a consonant → replace "y" with "ies"
  // Words ending in "y" after a vowel → add "s"
  if (word.endsWith("y")) {
    const secondToLast = word[word.length - 2];
    const vowels = "aeiou";
    if (secondToLast && vowels.includes(secondToLast.toLowerCase())) {
      // y after vowel → add "s"
      return `${word}s`;
    }
    // y after consonant → replace with "ies"
    return `${word.slice(0, -1)}ies`;
  }

  // Default → add "s"
  return `${word}s`;
};

const toFrameworkDisplayName = (name: string, category: string) => {
  if (name === "HIPAA") {
    return category === "Required" ? "HIPAA Required" : "HIPAA Addressable";
  }
  return name;
};

const toFrameworkHandle = (name: string, category: string) => {
  const base = name.toLowerCase().replace(/\s+/g, "-");
  if (name === "HIPAA") {
    return category === "Required" ? `${base}-required` : `${base}-addressable`;
  }
  return base;
};

const toFrameworkMetricHandle = (name: string, category: string) => {
  if (name === "HIPAA") {
    if (category === "Required") {
      return "hipaa-required";
    }
    if (category === "Addressable") {
      return "hipaa-addressable";
    }
  }
  if (name === "HITRUST" && category === "Inheritable") {
    return "hitrust-inheritable";
  }
  return `${name.toLowerCase()}-${category.toLowerCase()}`;
};

const deserializeFramework = (
  payload: DeployGlobalControlFrameworkResponse,
): DeployGlobalControlFramework => {
  const framework = payload._embedded?.framework;
  const name = framework?.name ?? "";
  const category = payload.category ?? "";
  const displayName = toFrameworkDisplayName(name, category);
  const metricHandle = toFrameworkHandle(name, category);

  return {
    id: `${payload.id ?? ""}`,
    frameworkId: `${framework?.id ?? ""}`,
    name,
    category,
    displayName,
    metricHandle,
  };
};

export const deserializeGlobalControl = (
  payload: DeployGlobalControlResponse,
): DeployGlobalControl => {
  const frameworks =
    payload._embedded?.global_control_frameworks?.map(deserializeFramework) ??
    [];
  const handle = payload.handle ?? "";

  return {
    id: `${payload.id ?? ""}`,
    name: payload.name ?? "",
    handle,
    description: payload.description ?? "",
    area: payload.area ?? "",
    callToAction: payload.call_to_action ?? payload.callToAction ?? "",
    detailedDescription:
      payload.detailed_description ?? payload.detailedDescription ?? "",
    hasRemediation: Boolean(
      payload.has_remediation ?? payload.hasRemediation ?? false,
    ),
    customFrontend: Boolean(
      payload.custom_frontend ?? payload.customFrontend ?? false,
    ),
    frameworks,
    frameworksAsString: frameworks
      .map((f) => f.displayName || `${f.name} ${f.category}`)
      .join(", "),
    frameworkHandles: frameworks.map((f) => f.metricHandle),
    isDedicatedOnly: DEDICATED_STACK_ONLY_CONTROL_HANDLES.includes(handle),
    isPremium: PREMIUM_CONTROL_HANDLES.includes(handle),
    isComingSoon: COMING_SOON_HANDLES.includes(handle),
    implementationUiComponent:
      CUSTOM_IMPLEMENTATION_COMPONENTS[handle] ?? "default",
    isOrganizationScoped: ORGANIZATION_SCOPED_CONTROL_HANDLES.includes(handle),
    globalControlFrameworkIds: frameworks.map((f) => f.id),
  };
};

const toResourceType = (value = "") => {
  if (!value) return "Resource";
  return RESOURCE_TYPE_DISPLAY[value] ?? value;
};

const toComparableString = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return `${value}`;
};

export const deserializeCustomerControlResource = (
  payload: DeployCustomerControlResourceResponse,
  customerControlId = "",
): DeployCustomerControlResource => {
  const id = `${payload.id ?? ""}`;
  const linkedCustomerControlId = extractIdFromLink(
    payload._links?.customer_control,
  );

  // Force use the passed customerControlId if provided
  const ownerId = customerControlId
    ? customerControlId
    : linkedCustomerControlId || "";

  const resourceType = payload.resource_type ?? payload.resourceType ?? "";
  const resourceId = toComparableString(
    payload.resource_id ?? payload.resourceId,
  );
  return {
    id,
    customerControlId: ownerId,
    compliant: Boolean(payload.compliant),
    ignored: Boolean(payload.ignored),
    detailsText: payload.details_text ?? payload.detailsText ?? "",
    resourceHandle: payload.resource_handle ?? payload.resourceHandle ?? "",
    resourceType,
    resourceId,
    readableResourceType:
      payload.readable_resource_type ??
      payload.readableResourceType ??
      toResourceType(resourceType),
    linkToParameters: {
      path: resourceId ? `/resources/${resourceType}/${resourceId}` : "",
      parentId: null,
      modelId: resourceId,
    },
  };
};

export const deserializeCustomerControl = (
  payload: DeployCustomerControlResponse,
): DeployCustomerControl => {
  const id = `${payload.id ?? ""}`;

  return {
    id,
    globalControlId:
      extractIdFromLink(payload._links.global_control) ??
      payload._embedded?.global_control?.id?.toString() ??
      "",
    accountId: extractIdFromLink(payload._links.account),
    status: payload.status ?? "",
    lastEvaluation: payload.last_evaluation ?? payload.lastEvaluation ?? "",
    resourceIds: [], // Will be populated later from the resources themselves
    isEvaluating: false,
    isDisabled: false,
    isComingSoon: false,
  };
};

export const globalControlEntities = {
  global_control: defaultEntity({
    id: "global_control",
    deserialize: deserializeGlobalControl,
    save: schema.globalControls.add,
  }),
};

export const globalControlFrameworkEntities = {
  global_control_framework: defaultEntity({
    id: "global_control_framework",
    deserialize: (payload: DeployGlobalControlFrameworkResponse) => {
      const framework = payload._embedded?.framework;
      const name = payload.name ?? framework?.name ?? "";
      const category = payload.category ?? "";
      return {
        id: `${payload.id}`,
        frameworkId: framework?.id ? `${framework.id}` : "",
        name,
        category,
        displayName: toFrameworkDisplayName(name, category),
        metricHandle: toFrameworkMetricHandle(name, category),
      };
    },
    save: schema.globalControlFrameworks.add,
  }),
};

export const customerControlEntities = {
  customer_control: defaultEntity({
    id: "customer_control",
    deserialize: deserializeCustomerControl,
    save: schema.customerControls.add,
  }),
};

export const customerControlResourceEntities = {
  customer_control_resource: defaultEntity({
    id: "customer_control_resource",
    deserialize: deserializeCustomerControlResource,
    save: schema.customerControlResources.add,
  }),
};

const groupResourcesByControl = (
  resources: DeployCustomerControlResource[],
) => {
  const map = new Map<string, DeployCustomerControlResource[]>();
  for (const resource of resources) {
    const list = map.get(resource.customerControlId);
    if (list) {
      list.push(resource);
    } else {
      map.set(resource.customerControlId, [resource]);
    }
  }
  return map;
};

const getLatestEvaluation = (a: string, b: string) => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

const formatRelative = (value: string) => {
  if (!value) return "";
  const dt = DateTime.fromISO(value, { zone: "utc" });
  if (!dt.isValid) return "";
  return dt.toRelative() ?? "";
};

export interface SecurityControlMetrics {
  totalInScope: number;
  totalCompliant: number;
  totalIgnored: number;
  resourceType: string;
  lastEvaluation: string;
  lastEvaluationInWords: string;
  progressPercentage: number;
  status: SecurityControlStatus;
  implementationSummary: string;
  implementationAsString: string;
  statusSort: number;
}

const createEmptyMetrics = (): SecurityControlMetrics => ({
  totalInScope: 0,
  totalCompliant: 0,
  totalIgnored: 0,
  resourceType: "Resource",
  lastEvaluation: "",
  lastEvaluationInWords: "",
  progressPercentage: 0,
  status: "NO_SCOPE",
  implementationSummary: "0 Resources",
  implementationAsString: "0 of 0 Resources (0%)",
  statusSort: 101,
});

const calculateStatus = (
  totalInScope: number,
  progressPercentage: number,
): SecurityControlStatus => {
  if (totalInScope === 0) return "NO_SCOPE";
  if (progressPercentage === 0) return "NOT_STARTED";
  if (progressPercentage < 100) return "PARTIAL";
  return "COMPLETE";
};

const buildMetrics = ({
  resources,
  isComingSoon,
  resourceType,
  lastEvaluation,
}: {
  resources: DeployCustomerControlResource[];
  isComingSoon: boolean;
  resourceType: string;
  lastEvaluation: string;
}): SecurityControlMetrics => {
  const activeResources = resources.filter((r) => !r.ignored);
  const ignoredResources = resources.filter((r) => r.ignored);
  const totalInScope = activeResources.length;
  const totalCompliant = isComingSoon
    ? 0
    : activeResources.filter((r) => r.compliant).length;
  const totalIgnored = ignoredResources.length;
  const progressPercentage =
    totalInScope === 0 ? 0 : Math.round((totalCompliant / totalInScope) * 100);
  const status = calculateStatus(totalInScope, progressPercentage);
  const lastEvaluationInWords = formatRelative(lastEvaluation);
  const implementationSummary = `${totalInScope} ${pluralize(resourceType, totalInScope)}`;
  const implementationAsString = `${totalCompliant} of ${totalInScope} ${pluralize(resourceType, totalInScope)} (${progressPercentage}%)`;
  const statusSort = totalInScope === 0 ? 101 : progressPercentage;

  return {
    totalInScope,
    totalCompliant,
    totalIgnored,
    resourceType,
    lastEvaluation,
    lastEvaluationInWords,
    progressPercentage,
    status,
    implementationSummary,
    implementationAsString,
    statusSort,
  };
};

type SecurityControlResourceIdentityMode = "resource" | "user" | "organization";

const getResourceIdentity = (
  resource: DeployCustomerControlResource,
  mode: SecurityControlResourceIdentityMode,
) => {
  if (mode === "organization") {
    return "organization";
  }

  const resourceId = toComparableString(resource.resourceId);
  const resourceHandle = toComparableString(resource.resourceHandle);
  const readableResourceType = toComparableString(
    resource.readableResourceType,
  );
  const resourceType = toComparableString(resource.resourceType);
  const id = toComparableString(resource.id);
  const fallbackIdentity = `${readableResourceType}:${resourceType}:${id}`;

  if (mode === "user") {
    return resourceHandle || resourceId || fallbackIdentity;
  }

  return resourceId || resourceHandle || fallbackIdentity;
};

const groupSecurityControlResources = (
  resources: DeployCustomerControlResource[],
  options: { identityMode: SecurityControlResourceIdentityMode },
) => {
  const groups = new Map<string, DeployCustomerControlResource[]>();

  for (const resource of resources) {
    const key = getResourceIdentity(resource, options.identityMode);
    const list = groups.get(key);
    if (list) {
      list.push(resource);
    } else {
      groups.set(key, [resource]);
    }
  }

  return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
};

const mergeResourceGroup = (
  resources: DeployCustomerControlResource[],
): DeployCustomerControlResource => {
  const sorted = [...resources].sort((a, b) =>
    toComparableString(a.id).localeCompare(toComparableString(b.id)),
  );
  const activeResources = sorted.filter((resource) => !resource.ignored);
  const isIgnored = activeResources.length === 0;
  const compliant = isIgnored
    ? sorted[0].compliant
    : activeResources.every((resource) => resource.compliant);
  const source = isIgnored
    ? sorted[0]
    : (activeResources.find((resource) => !resource.compliant) ??
      activeResources[0]);

  return {
    ...source,
    compliant,
    ignored: isIgnored,
    detailsText: source.detailsText,
  };
};

export const dedupeSecurityControlResources = (
  resources: DeployCustomerControlResource[],
  options: { identityMode: SecurityControlResourceIdentityMode },
) => {
  return groupSecurityControlResources(resources, options).map(([, group]) =>
    mergeResourceGroup(group),
  );
};

const getResourceIdentityMode = (
  globalControl: DeployGlobalControl,
): SecurityControlResourceIdentityMode => {
  if (globalControl.handle === "sso-integration") {
    return "organization";
  }

  if (globalControl.handle === "multi-factor-authentication") {
    return "user";
  }

  return "resource";
};

const buildCumulativeMetrics = (
  controls: SecurityControlCustomerControl[],
  globalControl: DeployGlobalControl,
) => {
  if (globalControl.isOrganizationScoped) {
    const resources = controls.flatMap((control) => control.resources);
    const identityMode = getResourceIdentityMode(globalControl);
    const dedupedResources = dedupeSecurityControlResources(resources, {
      identityMode,
    });
    const resourceType =
      identityMode === "organization"
        ? "Organization"
        : (dedupedResources[0]?.readableResourceType ??
          controls[0]?.metrics.resourceType ??
          "Resource");
    const lastEvaluation = controls.reduce(
      (latest, control) =>
        getLatestEvaluation(latest, control.metrics.lastEvaluation),
      "",
    );

    const metrics = buildMetrics({
      resources: dedupedResources,
      isComingSoon: globalControl.isComingSoon,
      resourceType,
      lastEvaluation,
    });

    return {
      ...metrics,
      implementationSummary: `${metrics.totalInScope} ${pluralize(
        metrics.resourceType,
        metrics.totalInScope,
      )}`,
    };
  }

  const cumulative = createEmptyMetrics();
  for (const customerControl of controls) {
    if (customerControl.isDisabled) {
      // Disabled scopes (dedicated-only on shared stack) count as 1 in-scope,
      // 0 compliant so the cumulative reflects uncovered environments.
      cumulative.totalInScope += 1;
      continue;
    }
    cumulative.totalInScope += customerControl.metrics.totalInScope;
    cumulative.totalIgnored += customerControl.metrics.totalIgnored;
    cumulative.totalCompliant += customerControl.metrics.totalCompliant;
    cumulative.resourceType = customerControl.metrics.resourceType;
    cumulative.lastEvaluation = getLatestEvaluation(
      cumulative.lastEvaluation,
      customerControl.metrics.lastEvaluation,
    );
  }

  cumulative.progressPercentage =
    cumulative.totalInScope === 0
      ? 0
      : Math.round((cumulative.totalCompliant / cumulative.totalInScope) * 100);
  cumulative.status = calculateStatus(
    cumulative.totalInScope,
    cumulative.progressPercentage,
  );
  cumulative.lastEvaluationInWords = formatRelative(cumulative.lastEvaluation);
  cumulative.implementationSummary = `${cumulative.totalInScope} ${pluralize(
    cumulative.resourceType,
    cumulative.totalInScope,
  )}`;
  cumulative.implementationAsString = `${cumulative.totalCompliant} of ${cumulative.totalInScope} ${pluralize(
    cumulative.resourceType,
    cumulative.totalInScope,
  )} (${cumulative.progressPercentage}%)`;
  cumulative.statusSort =
    cumulative.totalInScope === 0 ? 101 : cumulative.progressPercentage;

  return cumulative;
};

export interface SecurityControlCustomerControl extends DeployCustomerControl {
  metrics: SecurityControlMetrics;
  environmentId: string;
  environmentHandle: string;
  environmentType: "production" | "development";
  isDisabled: boolean;
  resources: DeployCustomerControlResource[];
}

export interface SecurityControlImplementationResourceGroup {
  identity: string;
  resource: DeployCustomerControlResource;
  resourceIds: string[];
}

export const buildMfaImplementationResourceGroups = (
  controls: SecurityControlCustomerControl[],
): SecurityControlImplementationResourceGroup[] => {
  const resources = controls.flatMap((control) => control.resources);

  return groupSecurityControlResources(resources, { identityMode: "user" }).map(
    ([identity, group]) => {
      const sorted = [...group].sort((a, b) =>
        toComparableString(a.id).localeCompare(toComparableString(b.id)),
      );

      return {
        identity,
        resource: mergeResourceGroup(sorted),
        resourceIds: sorted.map((resource) => resource.id),
      };
    },
  );
};

export interface SecurityControlAggregate {
  id: string;
  globalControl: DeployGlobalControl;
  customerControls: SecurityControlCustomerControl[];
  /** Lookup by environment id; built once per aggregate to avoid per-cell work in the table. */
  customerControlsByEnvironmentId: Record<
    string,
    SecurityControlCustomerControl
  >;
  cumulativeMetrics: SecurityControlMetrics;
}

export interface FrameworkMetric {
  handle: string;
  name: string;
  description: string;
  logo?: string;
  controlCount: number;
  totalControlResourcesCount: number;
  compliantControlResourcesCount: number;
  progressPercentage: number;
  isComplete: boolean;
  styleVariant: "success" | "warn" | "danger";
}

export interface SecurityControlFrameworkMetrics {
  [handle: string]: FrameworkMetric;
}

const toStyleVariant = (percentage: number): "success" | "warn" | "danger" => {
  if (percentage === 100) return "success";
  if (percentage === 0) return "danger";
  return "warn";
};

// When all customer controls for a global control are disabled (e.g.
// dedicated-only controls scoped exclusively to a shared stack), cumulative
// metrics has totalInScope=0, which would exclude the control from the
// framework score denominator and inflate scores toward 100%. Count it as
// 1 in-scope / 0 compliant so the score reflects that the control is not met.
const getScopeContribution = (
  customerControls: SecurityControlCustomerControl[],
  cumulative: SecurityControlMetrics,
): { scopeTotal: number; scopeCompliant: number } => {
  const allDisabled =
    customerControls.length > 0 && customerControls.every((c) => c.isDisabled);
  return {
    scopeTotal: allDisabled ? 1 : cumulative.totalInScope,
    scopeCompliant: allDisabled ? 0 : cumulative.totalCompliant,
  };
};

const buildFrameworkMetric = (
  handle: string,
  totalControlResourcesCount: number,
  compliant: number,
  controlCount: number,
): FrameworkMetric => {
  const meta = FRAMEWORK_DESCRIPTIONS[handle];
  const progress =
    totalControlResourcesCount === 0
      ? 0
      : Math.round((compliant / totalControlResourcesCount) * 100);
  return {
    handle,
    name: meta?.name ?? handle,
    description: meta?.description ?? "",
    logo: meta?.logo,
    controlCount,
    totalControlResourcesCount,
    compliantControlResourcesCount: compliant,
    progressPercentage: progress,
    isComplete: progress === 100,
    styleVariant: toStyleVariant(progress),
  };
};

export const PRINT_REPORT_FRAMEWORK_ORDER = [
  "hipaa-required",
  "hipaa-addressable",
  "hitrust-inheritable",
] as const;

/** Derive framework compliance metrics from a (possibly filtered) control list. */
export const computeFrameworkMetricsFromAggregates = (
  controls: SecurityControlAggregate[],
  options: { metricHandles?: string[] } = {},
): SecurityControlFrameworkMetrics => {
  const allowedHandles =
    options.metricHandles && options.metricHandles.length > 0
      ? new Set(options.metricHandles)
      : null;

  const frameworkTotals: Record<
    string,
    { controlCount: number; total: number; compliant: number }
  > = Object.keys(FRAMEWORK_DESCRIPTIONS).reduce(
    (acc, handle) => {
      acc[handle] = { controlCount: 0, total: 0, compliant: 0 };
      return acc;
    },
    {} as Record<
      string,
      { controlCount: number; total: number; compliant: number }
    >,
  );

  for (const aggregate of controls) {
    const {
      globalControl,
      cumulativeMetrics: cumulative,
      customerControls,
    } = aggregate;

    const { scopeTotal, scopeCompliant } = getScopeContribution(
      customerControls,
      cumulative,
    );

    const total = frameworkTotals.all;
    total.controlCount += 1;
    total.total += scopeTotal;
    total.compliant += scopeCompliant;

    for (const framework of globalControl.frameworks) {
      const handle = framework.metricHandle;
      if (allowedHandles && !allowedHandles.has(handle)) {
        continue;
      }
      if (!frameworkTotals[handle]) {
        frameworkTotals[handle] = { controlCount: 0, total: 0, compliant: 0 };
      }
      const meta = frameworkTotals[handle];
      meta.controlCount += 1;
      meta.total += scopeTotal;
      meta.compliant += scopeCompliant;
    }
  }

  const frameworks: SecurityControlFrameworkMetrics = {};
  Object.entries(frameworkTotals).forEach(([handle, totals]) => {
    frameworks[handle] = buildFrameworkMetric(
      handle,
      totals.total,
      totals.compliant,
      totals.controlCount,
    );
  });

  return frameworks;
};

const selectGlobalControlsAsList = schema.globalControls.selectTableAsList;
const selectCustomerControlsAsList = schema.customerControls.selectTableAsList;
const selectCustomerControlResourcesAsList =
  schema.customerControlResources.selectTableAsList;

export const selectSecurityControlEnvironments = createSelector(
  selectEnvironmentsAsList,
  selectPermissionsAsList,
  selectRolesByOrgId,
  (_: WebState, props: { orgId: string }) => props.orgId,
  (envs, perms, roles, orgId) => {
    const rolesInOrg = roles.filter((role) => role.organizationId === orgId);
    const ownerRoleTypes = new Set(["owner", "platform_owner"]);
    const userIsOwner = rolesInOrg.some((r) => ownerRoleTypes.has(r.type));
    const roleIds = new Set(rolesInOrg.map((r) => r.id));

    const environments = envs
      .filter((env) => env.organizationId === orgId)
      .map((env) => {
        const envPerms = perms.filter((perm) => perm.environmentId === env.id);
        const userPerms = envPerms.filter((perm) => roleIds.has(perm.roleId));
        const hasAccess = userIsOwner || userPerms.length > 0;
        return {
          id: env.id,
          handle: env.handle,
          type: env.type,
          stackId: env.stackId,
          hasAccess,
        };
      })
      .filter((env) => env.hasAccess)
      .sort((a, b) => a.handle.localeCompare(b.handle));

    const production = environments.filter((env) => env.type === "production");
    const development = environments.filter(
      (env) => env.type === "development",
    );

    return {
      all: environments,
      production,
      development,
    };
  },
);

export const selectSecurityControlDefaultScope = createSelector(
  selectSecurityControlEnvironments,
  (envs) => {
    const source =
      envs.production.length > 0 ? envs.production : envs.development;
    return source.slice(0, MAX_SECURITY_CONTROL_SCOPE).map((env) => env.id);
  },
);

export const selectSecurityControlRequiresDedicatedStack = createSelector(
  selectSecurityControlEnvironments,
  (envs) => envs.production.length === 0 && envs.all.length > 0,
);

export interface SecurityControlViewModel {
  controls: SecurityControlAggregate[];
  frameworks: SecurityControlFrameworkMetrics;
  categories: string[];
}

export const selectSecurityControlAggregates = createSelector(
  selectGlobalControlsAsList,
  selectCustomerControlsAsList,
  selectCustomerControlResourcesAsList,
  selectEnvironmentsAsList,
  (_: WebState, props: { orgId: string; scopeIds: string[] }) => props,
  (globalControls, customerControls, resources, environments, props) => {
    const envMap = environments
      .filter((env) => env.organizationId === props.orgId)
      .reduce<Record<string, (typeof environments)[number]>>((acc, env) => {
        acc[env.id] = env;
        return acc;
      }, {});

    const scopeSet = props.scopeIds.length > 0 ? new Set(props.scopeIds) : null;

    const controlMap = globalControls.reduce<
      Record<string, DeployGlobalControl>
    >((acc, control) => {
      acc[control.id] = control;
      return acc;
    }, {});

    const resourceMap = groupResourcesByControl(resources);

    const enrichedControls: SecurityControlCustomerControl[] = [];
    for (const customerControl of customerControls) {
      const environment = envMap[customerControl.accountId];
      if (!environment) continue;
      if (scopeSet && !scopeSet.has(environment.id)) continue;
      const globalControl = controlMap[customerControl.globalControlId];
      if (!globalControl) continue;

      const controlResources = resourceMap.get(customerControl.id) ?? [];
      const resourceType =
        controlResources[0]?.readableResourceType ?? "Resource";
      const metrics = buildMetrics({
        resources: controlResources,
        isComingSoon: globalControl.isComingSoon,
        resourceType,
        lastEvaluation: customerControl.lastEvaluation ?? "",
      });

      const isDisabled =
        globalControl.isDedicatedOnly && environment.type === "development";

      enrichedControls.push({
        ...customerControl,
        metrics,
        environmentId: environment.id,
        environmentHandle: environment.handle,
        environmentType: environment.type,
        isDisabled,
        resources: controlResources,
      });
    }

    const groupedByGlobal = new Map<string, SecurityControlCustomerControl[]>();
    for (const control of enrichedControls) {
      const list = groupedByGlobal.get(control.globalControlId);
      if (list) {
        list.push(control);
      } else {
        groupedByGlobal.set(control.globalControlId, [control]);
      }
    }

    const aggregates: SecurityControlAggregate[] = [];
    const frameworkTotals: Record<
      string,
      { controlCount: number; total: number; compliant: number }
    > = Object.keys(FRAMEWORK_DESCRIPTIONS).reduce(
      (acc, handle) => {
        acc[handle] = { controlCount: 0, total: 0, compliant: 0 };
        return acc;
      },
      {} as Record<
        string,
        { controlCount: number; total: number; compliant: number }
      >,
    );

    const categories = new Set<string>();

    for (const globalControl of globalControls) {
      categories.add(globalControl.area);
      const customerControlList = groupedByGlobal.get(globalControl.id) ?? [];
      const cumulative = buildCumulativeMetrics(
        customerControlList,
        globalControl,
      );

      const customerControlsByEnvironmentId = customerControlList.reduce<
        Record<string, SecurityControlCustomerControl>
      >((acc, c) => {
        acc[c.environmentId] = c;
        return acc;
      }, {});

      aggregates.push({
        id: globalControl.id,
        globalControl,
        customerControls: customerControlList,
        customerControlsByEnvironmentId,
        cumulativeMetrics: cumulative,
      });

      const { scopeTotal, scopeCompliant } = getScopeContribution(
        customerControlList,
        cumulative,
      );

      const total = frameworkTotals.all;
      total.controlCount += 1;
      total.total += scopeTotal;
      total.compliant += scopeCompliant;

      for (const framework of globalControl.frameworks) {
        if (!frameworkTotals[framework.metricHandle]) {
          frameworkTotals[framework.metricHandle] = {
            controlCount: 0,
            total: 0,
            compliant: 0,
          };
        }
        const meta = frameworkTotals[framework.metricHandle];
        meta.controlCount += 1;
        meta.total += scopeTotal;
        meta.compliant += scopeCompliant;
      }
    }

    const frameworks: SecurityControlFrameworkMetrics = {};
    Object.entries(frameworkTotals).forEach(([handle, totals]) => {
      frameworks[handle] = buildFrameworkMetric(
        handle,
        totals.total,
        totals.compliant,
        totals.controlCount,
      );
    });

    return {
      controls: aggregates,
      frameworks,
      categories: Array.from(categories).sort((a, b) => a.localeCompare(b)),
    };
  },
);

export const fetchGlobalControls = api.get("/global_controls?per_page=500");

export const fetchCustomerControls = api.get<{
  orgId: string;
  accountIds: string[];
}>("/customer_controls", function* (ctx, next) {
  if (ctx.payload.orgId === "" || ctx.payload.accountIds.length === 0) {
    return;
  }

  const params = new URLSearchParams();
  params.set("per_page", "500");
  params.set("organization_id", ctx.payload.orgId);
  ctx.payload.accountIds
    .slice(0, MAX_SECURITY_CONTROL_SCOPE)
    .forEach((id) => params.append("account_ids[]", id));
  params.append("include[]", "customer_control_resources");

  const query = params.toString();
  ctx.request = ctx.req({
    url: `${ctx.req().url}?${query}`,
  });

  yield* next();

  // Manually process to ensure proper linking
  if (ctx.json.ok) {
    // Clear and manually process the data to ensure correct linking
    if (ctx.json.value._embedded?.customer_controls) {
      // Remove only controls/resources for the specific accountIds being fetched
      const accountIds = ctx.payload.accountIds;
      const existingControls = yield* select(selectCustomerControlsAsList);
      const existingResources = yield* select(
        selectCustomerControlResourcesAsList,
      );

      // Find controls that match the accountIds being fetched
      const controlsToRemove = existingControls
        .filter((control) => accountIds.includes(control.accountId))
        .map((control) => control.id);

      // Find resources that belong to the controls being removed
      const controlIdsSet = new Set(controlsToRemove);
      const resourcesToRemove = existingResources
        .filter((resource) => controlIdsSet.has(resource.customerControlId))
        .map((resource) => resource.id);

      const evaluatingIds = new Set(
        existingControls
          .filter((control) => control.isEvaluating)
          .map((c) => c.id),
      );

      const controlsToSave: Record<string, DeployCustomerControl> = {};
      const resourcesToSave: Record<string, DeployCustomerControlResource> = {};

      for (const control of ctx.json.value._embedded.customer_controls) {
        const deserializedControl = deserializeCustomerControl(control);
        if (evaluatingIds.has(deserializedControl.id)) {
          deserializedControl.isEvaluating = true;
        }
        controlsToSave[deserializedControl.id] = deserializedControl;

        const controlId = deserializedControl.id;

        if (control._embedded?.customer_control_resources) {
          for (const resource of control._embedded.customer_control_resources) {
            const deserializedResource = deserializeCustomerControlResource(
              resource,
              controlId,
            );
            resourcesToSave[deserializedResource.id] = deserializedResource;
          }
        }
      }

      yield* schema.update([
        ...(controlsToRemove.length > 0
          ? [schema.customerControls.remove(controlsToRemove)]
          : []),
        ...(resourcesToRemove.length > 0
          ? [schema.customerControlResources.remove(resourcesToRemove)]
          : []),
        schema.customerControls.add(controlsToSave),
        schema.customerControlResources.add(resourcesToSave),
      ]);

      // Clear the embedded data to prevent HAL parser from overwriting
      ctx.json.value._embedded = {};
    }
  }
});

export const patchCustomerControlResource = api.patch<{
  id: string;
  ignored: boolean;
}>("/customer_control_resources/:id", function* (ctx, next) {
  const body = {
    ignored: ctx.payload.ignored,
  };
  ctx.request = ctx.req({
    body: JSON.stringify(body),
  });
  yield* next();
});

export const runCustomerControlEvaluation = api.post<{ id: string }>(
  "/customer_controls/:id/run",
  function* (ctx, next) {
    yield* next();

    if (ctx.json.ok && ctx.json.value._embedded) {
      ctx.json.value._embedded = {};
    }
  },
);

const CUSTOMER_CONTROL_EVALUATION_POLL_ATTEMPTS = 10;
const CUSTOMER_CONTROL_EVALUATION_POLL_INTERVAL = 3000;
const CUSTOMER_CONTROL_EVALUATION_TIMEOUT_MESSAGE =
  "We could not confirm the control rerun completed. Refresh the page in a few minutes to check the latest evaluation.";

export const updateCustomerControlResource = thunks.create<{
  id: string;
  ignored: boolean;
  orgId?: string;
  accountIds?: string[];
}>("security-controls:update-resource", function* (ctx, next) {
  const { id, ignored, orgId, accountIds } = ctx.payload;
  const previous = yield* select((state: WebState) =>
    schema.customerControlResources.selectById(state, { id }),
  );

  yield* schema.update(
    schema.customerControlResources.patch({ [id]: { ignored } }),
  );

  const response = yield* patchCustomerControlResource.run(ctx.payload);
  if (!response.json.ok && previous.id !== "") {
    yield* schema.update(
      schema.customerControlResources.patch({
        [id]: { ignored: previous.ignored },
      }),
    );
  } else {
    // Background refresh to ensure aggregates and lists are fresh
    if (orgId && accountIds && accountIds.length > 0) {
      yield* fetchCustomerControls.run({ orgId, accountIds });
    }
  }

  yield* next();
});

export const updateCustomerControlResources = thunks.create<{
  ids: string[];
  ignored: boolean;
  orgId?: string;
  accountIds?: string[];
}>("security-controls:update-resources", function* (ctx, next) {
  const { ids, ignored, orgId, accountIds } = ctx.payload;
  const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
  if (uniqueIds.length === 0) {
    yield* next();
    return;
  }

  const previousById: Record<string, boolean> = {};
  const optimisticPatch: Record<string, { ignored: boolean }> = {};

  for (const id of uniqueIds) {
    const previous = yield* select((state: WebState) =>
      schema.customerControlResources.selectById(state, { id }),
    );
    if (previous.id !== "") {
      previousById[id] = previous.ignored;
      optimisticPatch[id] = { ignored };
    }
  }

  const patchIds = Object.keys(optimisticPatch);
  if (patchIds.length === 0) {
    yield* next();
    return;
  }

  yield* schema.update(schema.customerControlResources.patch(optimisticPatch));

  const failedIds: string[] = [];
  let succeeded = false;
  for (const id of patchIds) {
    const response = yield* patchCustomerControlResource.run({ id, ignored });
    if (!response.json.ok) {
      failedIds.push(id);
    } else {
      succeeded = true;
    }
  }

  if (failedIds.length > 0) {
    const rollbackPatch = failedIds.reduce<
      Record<string, { ignored: boolean }>
    >((acc, id) => {
      acc[id] = { ignored: previousById[id] };
      return acc;
    }, {});
    yield* schema.update(schema.customerControlResources.patch(rollbackPatch));
  }

  if (succeeded && orgId && accountIds && accountIds.length > 0) {
    yield* fetchCustomerControls.run({ orgId, accountIds });
  }

  yield* next();
});

export const evaluateCustomerControl = thunks.create<{
  id: string;
  orgId: string;
  accountIds: string[];
}>("security-controls:evaluate", [
  thunkLoader,
  function* (ctx, next) {
    const { id, orgId, accountIds } = ctx.payload;
    try {
      const before = yield* select((state: WebState) =>
        schema.customerControls.selectById(state, { id }),
      );
      const originalEvaluation = before.lastEvaluation;

      yield* schema.update(
        schema.customerControls.patch({ [id]: { isEvaluating: true } }),
      );

      const response = yield* runCustomerControlEvaluation.run({ id });

      if (!response.json.ok) {
        throw new Error(response.json.error.message);
      }

      if (orgId !== "" && accountIds.length > 0) {
        yield* fetchCustomerControls.run({ orgId, accountIds });
        const afterRefresh = yield* select((state: WebState) =>
          schema.customerControls.selectById(state, { id }),
        );
        let confirmed = afterRefresh.lastEvaluation !== originalEvaluation;

        for (
          let attempt = 0;
          !confirmed && attempt < CUSTOMER_CONTROL_EVALUATION_POLL_ATTEMPTS;
          attempt++
        ) {
          yield* delay(CUSTOMER_CONTROL_EVALUATION_POLL_INTERVAL);
          yield* fetchCustomerControls.run({ orgId, accountIds });
          const updated = yield* select((state: WebState) =>
            schema.customerControls.selectById(state, { id }),
          );

          if (updated.lastEvaluation !== originalEvaluation) {
            confirmed = true;
            break;
          }
        }

        if (!confirmed) {
          throw new Error(CUSTOMER_CONTROL_EVALUATION_TIMEOUT_MESSAGE);
        }
      }
    } finally {
      yield* schema.update(
        schema.customerControls.patch({ [id]: { isEvaluating: false } }),
      );
    }

    yield* next();
  },
]);
